import { capitalize } from '@fastre/core/helperFunctions/string'
import { ListingTenantInfoSchema } from '@fastre/core/schemas/saleListing'
import { zodResolver } from '@hookform/resolvers/zod'
import { DeleteRounded } from '@mui/icons-material'
import {
    Box,
    Button,
    Divider,
    IconButton,
    Option,
    Radio,
    RadioGroup,
    Sheet,
    Stack,
    Typography,
} from '@mui/joy'
import { useApi } from 'api'
import AgentInput from 'components/agentInput'
import { SlotInput, SlotWrapper } from 'components/input'
import LocationAutocomplete from 'components/LocationAutocomplete'
import Select from 'components/select'
import { useShowSnack } from 'components/snackbar'
import { MobileFriendlyStack } from 'components/stack'
import UnsavedModal from 'components/unsavedModal'
import { prop } from 'ramda'
import { useImperativeHandle, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { useBlocker } from 'react-router'
import { useEditMode } from './editMode'
import FormStepper from './formStepper'
import { useListingContext } from './listingProvider'

const TenantInfo = ({ ref }) => {
    const api = useApi()
    const showSnack = useShowSnack()
    const { listing } = useListingContext()
    const { editMode } = useEditMode()

    const [loading, setLoading] = useState(false)

    const hookForm = useForm<ListingTenantInfoSchema>({
        defaultValues: listing.occupantStatus === 'investment' ? listing : undefined,
        resolver: zodResolver(ListingTenantInfoSchema),
    })

    const { control, handleSubmit, formState, getValues, setValue, trigger, reset, watch, setError } =
        hookForm

    const {
        fields: tenants,
        append: appendTenant,
        remove: removeTenant,
    } = useFieldArray({
        control,
        name: 'tenancyInfo.tenants',
    })

    const disabled = false
    const formDisabled = false

    const managingAgentType = watch('tenancyInfo.managingAgent.type')

    const onSubmit: SubmitHandler<ListingTenantInfoSchema> = async data => {
        try {
            await api.post(`/listing/sale/${listing.listingId}/tenancyinfo`, data).then(prop('data'))
            showSnack('Tenancy info saved', 'success')
            return true
        } catch (e) {
            console.error(e)
            showSnack('Error saving tenancy info', 'danger')
            return false
        }
    }

    const handleSubmitPromise = (): Promise<boolean> => {
        return new Promise((resolve, reject) => {
            handleSubmit(
                async data => {
                    try {
                        const successVal = (await onSubmit(data)) as boolean
                        resolve(successVal)
                    } catch (error) {
                        resolve(false)
                    }
                },
                error => {
                    resolve(false)
                },
            )()
        })
    }

    useImperativeHandle(ref, () => ({
        saveForm: async () => {
            console.log('save form')

            const a = await handleSubmitPromise()
            console.log('saved success', a)
            return a
        },
        resetForm: () => reset(listing as any),
    }))

    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        //return false
        console.log('dirty fields', formState.dirtyFields)
        console.log('is dirty', formState.isDirty)
        return (
            currentLocation.pathname !== nextLocation.pathname &&
            formState.isDirty &&
            Object.keys(formState.dirtyFields).length > 0
        )
    })

    return (
        <FormProvider {...hookForm}>
            <form
                noValidate
                onSubmit={handleSubmit(onSubmit)}
            >
                <Stack
                    gap={2}
                    sx={{ width: '500px' }}
                >
                    <Typography level="h4">Managing Agent</Typography>
                    {editMode && (
                        <Controller
                            control={control}
                            disabled={disabled}
                            name="tenancyInfo.managingAgent.type"
                            render={field => (
                                <SlotWrapper
                                    {...field}
                                    formControlProps={{
                                        sx: {
                                            justifyContent: 'flex-end',
                                        },
                                    }}
                                >
                                    <RadioGroup
                                        onChange={e => {
                                            field.field.onChange(e)
                                            //setValue(`conjunctionalAgreements.${index}.type`, e.target.value as any)
                                        }}
                                    >
                                        <Radio
                                            value="internal"
                                            label="Internal"
                                            disabled={disabled}
                                        />
                                        <Radio
                                            value="external"
                                            label="External"
                                            disabled={disabled}
                                        />
                                    </RadioGroup>
                                </SlotWrapper>
                            )}
                        />
                    )}
                    {managingAgentType === 'internal' && (
                        <Controller
                            name="tenancyInfo.managingAgent.userId"
                            control={control}
                            disabled={disabled}
                            render={field => (
                                <SlotWrapper
                                    {...field}
                                    label="Agent"
                                >
                                    <AgentInput {...field.field} />
                                </SlotWrapper>
                            )}
                        />
                    )}
                    {managingAgentType === 'external' && (
                        <Stack gap={2}>
                            <Controller
                                name="tenancyInfo.managingAgent.name"
                                control={control}
                                disabled={disabled}
                                render={field => (
                                    <SlotInput
                                        {...field}
                                        label="Name"
                                    />
                                )}
                            />
                            <Controller
                                name="tenancyInfo.managingAgent.email"
                                control={control}
                                disabled={disabled}
                                render={field => (
                                    <SlotInput
                                        {...field}
                                        label="Email"
                                    />
                                )}
                            />
                            <Controller
                                name="tenancyInfo.managingAgent.mobileNumber"
                                control={control}
                                disabled={disabled}
                                render={field => (
                                    <SlotInput
                                        {...field}
                                        label="Mobile Number"
                                    />
                                )}
                            />
                            <Controller
                                name="tenancyInfo.managingAgent.agencyName"
                                control={control}
                                disabled={disabled}
                                render={field => (
                                    <SlotInput
                                        {...field}
                                        label="Agency Name"
                                    />
                                )}
                            />
                            <LocationAutocomplete
                                label="Agency Address"
                                name="tenancyInfo.managingAgent.agencyAddress"
                                control={control}
                                disabled={disabled}
                            />
                        </Stack>
                    )}
                </Stack>
                <Divider sx={{ mt: 2, mb: 3 }} />
                <Stack
                    gap={2}
                    sx={{ width: '500px' }}
                >
                    <Typography
                        level="h4"
                        sx={{ mt: 2 }}
                    >
                        Tenancy
                    </Typography>
                    <MobileFriendlyStack gap={2}>
                        <Controller
                            name="tenancyInfo.tenantLeaseStart"
                            control={control}
                            render={field => (
                                <SlotInput
                                    {...field}
                                    label="Lease Start"
                                    type="date"
                                    formControlProps={{
                                        sx: { flex: 1 },
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="tenancyInfo.tenantLeaseEnd"
                            control={control}
                            render={field => (
                                <SlotInput
                                    {...field}
                                    label="Lease End"
                                    type="date"
                                    formControlProps={{
                                        sx: { flex: 1 },
                                    }}
                                />
                            )}
                        />
                    </MobileFriendlyStack>
                    <MobileFriendlyStack gap={2}>
                        <Controller
                            name="tenancyInfo.rentalAmount"
                            control={control}
                            render={field => (
                                <SlotInput
                                    {...field}
                                    label="Rental Amount"
                                    type="dollar"
                                    formControlProps={{
                                        sx: { flex: 1 },
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="tenancyInfo.rentalFrequency"
                            control={control}
                            render={field => (
                                <SlotWrapper
                                    {...field}
                                    label="Frequency"
                                >
                                    <Select
                                        {...field.field}
                                        onChange={(e, value) => field.field.onChange(value)}
                                    >
                                        {['weekly', 'fortnightly', 'monthly'].map(type => (
                                            <Option
                                                key={type}
                                                value={type}
                                            >
                                                {capitalize(type)}
                                            </Option>
                                        ))}
                                    </Select>
                                </SlotWrapper>
                            )}
                        />
                    </MobileFriendlyStack>
                </Stack>
                <Stack
                    gap={2}
                    sx={{ mt: 2, width: '500px' }}
                >
                    {tenants.map((tenant, i) => (
                        <Sheet
                            key={tenant.id}
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderRadius: 'md',
                                mt: 2,
                            }}
                        >
                            <Stack
                                direction="row"
                                sx={{ mb: 2, justifyContent: 'space-between' }}
                            >
                                <Typography>Tenant {i + 1}</Typography>
                                {!disabled && editMode && (
                                    <Box>
                                        <IconButton
                                            variant="outlined"
                                            onClick={() => removeTenant(i)}
                                            size="sm"
                                        >
                                            <DeleteRounded fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Stack>
                            <Stack gap={2}>
                                <Stack gap={2}>
                                    <Controller
                                        name={`tenancyInfo.tenants.${i}.name`}
                                        disabled={disabled}
                                        control={control}
                                        render={field => (
                                            <SlotInput
                                                label="Name"
                                                {...field}
                                                formControlProps={{
                                                    sx: { flex: 1 },
                                                }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`tenancyInfo.tenants.${i}.email`}
                                        control={control}
                                        render={field => (
                                            <SlotInput
                                                label="Email"
                                                {...field}
                                                formControlProps={{
                                                    sx: { flex: 1 },
                                                }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`tenancyInfo.tenants.${i}.mobileNumber`}
                                        control={control}
                                        render={field => (
                                            <SlotInput
                                                label="Mobile Number"
                                                {...field}
                                                formControlProps={{
                                                    sx: { flex: 1 },
                                                }}
                                            />
                                        )}
                                    />
                                </Stack>
                            </Stack>
                        </Sheet>
                    ))}
                </Stack>
                {!formDisabled && editMode && (
                    <Button
                        variant="soft"
                        sx={{ mt: 2 }}
                        onClick={() =>
                            appendTenant({
                                name: '',
                            })
                        }
                    >
                        Add Tenant
                    </Button>
                )}
            </form>
            <Divider sx={{ mt: 2, mb: 5 }} />
            <Box sx={{ mt: 4 }}>
                <FormStepper
                    title="Tenant Consent Open Home Form"
                    formType="tenantConsentOpenHomeForm"
                    createDisabled={formDisabled || tenants.length == 0}
                    validate={async () => {
                        var shouldFocus = true
                        /*const block = await Promise.all(
                            requiredValues.map(async reqVal => {
                                const error = getValues(reqVal) == undefined

                                if (error) {
                                    setError(
                                        reqVal,
                                        {
                                            type: 'required',
                                            message: 'Required for Form 6',
                                        },
                                        {
                                            shouldFocus,
                                        },
                                    )

                                    shouldFocus = false
                                    console.log('error', reqVal)
                                }
                                return error
                            }),
                        )
                        if (block.some(x => x == true)) {
                            console.log('block time', Date.now() - start)
                            return false
                        }*/
                        return await trigger()
                    }}
                    preCreate={async () => {
                        if (formState.isDirty) {
                            return await handleSubmitPromise()
                        } else {
                            return true
                        }
                    }}
                />
            </Box>

            <Box sx={{ mt: 6 }}>
                <FormStepper
                    title="Notice of Intention to Sell Form"
                    formType="intentToSellForm"
                    createDisabled={formDisabled || tenants.length == 0}
                    validate={async () => {
                        var shouldFocus = true

                        /*const block = await Promise.all(
                                        requiredValues.map(async reqVal => {
                                            const error = getValues(reqVal) == undefined

                                            if (error) {
                                                setError(
                                                    reqVal,
                                                    {
                                                        type: 'required',
                                                        message: 'Required for Form 6',
                                                    },
                                                    {
                                                        shouldFocus,
                                                    },
                                                )

                                                shouldFocus = false
                                                console.log('error', reqVal)
                                            }
                                            return error
                                        }),
                                    )
                                    if (block.some(x => x == true)) {
                                        console.log('block time', Date.now() - start)
                                        return false
                                    }*/
                        return await trigger()
                    }}
                    preCreate={async () => {
                        if (formState.isDirty) {
                            return await handleSubmitPromise()
                        } else {
                            return true
                        }
                    }}
                />
            </Box>

            <Box sx={{ mt: 6 }}>
                <FormStepper
                    title="Entry Notice Form"
                    formType="entryNoticeForm"
                    createDisabled={formDisabled || tenants.length == 0}
                    validate={async () => {
                        var shouldFocus = true

                        /*const block = await Promise.all(
                            requiredValues.map(async reqVal => {
                                const error = getValues(reqVal) == undefined

                                if (error) {
                                    setError(
                                        reqVal,
                                        {
                                            type: 'required',
                                            message: 'Required for Form 6',
                                        },
                                        {
                                            shouldFocus,
                                        },
                                    )

                                    shouldFocus = false
                                    console.log('error', reqVal)
                                }
                                return error
                            }),
                        )
                        if (block.some(x => x == true)) {
                            console.log('block time', Date.now() - start)
                            return false
                        }*/
                        return await trigger()
                    }}
                    preCreate={async () => {
                        if (formState.isDirty) {
                            return await handleSubmitPromise()
                        } else {
                            return true
                        }
                    }}
                />
            </Box>
            <UnsavedModal
                blocker={blocker}
                save={(onSuccess, onFail) => handleSubmitPromise().then(x => (x ? onSuccess() : onFail()))}
            />
        </FormProvider>
    )
}

export default TenantInfo
